import React, { useCallback } from "react";
import { CmsContentElementMap } from "./elements";
import { stripBannedAttributes } from "./utils";
import parse, { HTMLReactParserOptions } from "html-react-parser";

export interface CmsContent {
  /** Raw unparsed HTML string */
  content: string;
}

export const parseReplace: HTMLReactParserOptions["replace"] = (domNode) => {
  const newNode = domNode;
  newNode.attribs =
    domNode.attribs && Object.keys(domNode.attribs).length ? stripBannedAttributes(domNode.attribs) : {};

  // find short code options first as these won't have children (the content will be grabbed from ACF)
  if (newNode.data) {
    const matchingShortcode = newNode.data.replace(/[\[\]']+/g, "");

    return CmsContentElementMap.get(matchingShortcode.toLowerCase())?.(newNode);
  }

  if (!newNode.children)
    // No children to parse
    return null;

  // Find element match with class
  if (newNode.attribs.class) {
    const matchingClassElement = newNode.attribs.class.split(".").find((c) => CmsContentElementMap.has(c.trim()));

    if (matchingClassElement) {
      return CmsContentElementMap.get(matchingClassElement.toLowerCase())?.(newNode);
    }
  }

  // Find element match with tag name
  if (newNode.name && CmsContentElementMap.has(newNode.name.toLowerCase())) {
    return CmsContentElementMap.get(newNode.name.toLowerCase())?.(newNode);
  }

  return newNode;
};

/**
 * Takes HTML input from a CMS and attempts to parse it into React components
 *
 * @param content - HTML content to be parsed
 * @returns Returns the React components or elements from raw content
 *
 */
export const CmsContent: React.FC<CmsContent> = ({ content }) => {
  const parseHtml = useCallback(
    () =>
      !content || typeof content !== "string"
        ? null
        : parse(content, {
            replace: parseReplace,
          }),
    [content],
  );
  return <>{parseHtml()}</>;
};
